import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

const getStoredIds = () => {
  try {
    const ids = JSON.parse(window.localStorage.getItem('tvara-wishlist') || '[]');
    return Array.isArray(ids) ? ids.map((item) => String(item.id)).filter((id) => /^\d+$/.test(id)) : [];
  } catch {
    return [];
  }
};

const getSharedIds = () => {
  const ids = new URLSearchParams(window.location.search).get('share');
  return ids ? ids.split(',').filter((id) => /^\d+$/.test(id)) : [];
};

const getProductsHTML = async (sectionId, ids) => {
  if (!ids.length) return null;

  const markups = await Promise.all(
    ids.map((id) => {
      const url = new URL('/search', window.location.origin);
      url.searchParams.set('q', `id:${id}`);
      url.searchParams.set('resources[type]', 'product');
      return sectionRenderer.getSectionHTML(sectionId, false, url);
    })
  );

  const products = [];
  markups.forEach((markup) => {
    const rendered = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-wishlist-products]');
    if (rendered) products.push(...rendered.children);
  });

  return products;
};

const setStatus = (page, message) => {
  const status = page.querySelector('[data-wishlist-status]');
  if (status) status.textContent = message;
};

const removeStoredWishlistItem = (productId) => {
  try {
    const items = JSON.parse(window.localStorage.getItem('tvara-wishlist') || '[]');
    const nextItems = Array.isArray(items)
      ? items.filter((item) => String(item.id) !== String(productId))
      : [];
    window.localStorage.setItem('tvara-wishlist', JSON.stringify(nextItems));
    window.dispatchEvent(new CustomEvent('tvara:wishlist-change', { detail: { items: nextItems } }));
  } catch {
    // Ignore malformed wishlist storage; the cart add has already succeeded.
  }
};

const updateEmptyState = (page) => {
  const grid = page.querySelector('[data-wishlist-products]');
  const empty = page.querySelector('[data-wishlist-empty]');
  const category = page.querySelector('[data-wishlist-shop-by-category]');
  const hasProducts = grid?.querySelector('[data-wishlist-item]') !== null;
  if (empty) empty.hidden = hasProducts;
  if (category) category.hidden = hasProducts;
};

const loadProducts = async (page, ids, target) => {
  if (!target) return;
  if (!ids.length) {
    target.replaceChildren();
    return;
  }

  try {
    const products = await getProductsHTML(page.dataset.sectionId, ids);
    if (!products?.length) return;
    target.replaceChildren(...products);
  } catch (error) {
    console.warn('Wishlist products could not be loaded.', error);
  }
};

const initWishlistPage = (page) => {
  const sharedIds = getSharedIds();
  const wishlistGrid = page.querySelector('[data-wishlist-products]');
  const wishlistPanel = page.querySelector('[data-wishlist-panel]');
  const recent = page.querySelector('[data-wishlist-recently-viewed]');
  const recentGrid = page.querySelector('[data-wishlist-recent-products]');
  const recentEmpty = page.querySelector('[data-wishlist-recent-empty]');
  const loginDialog = page.querySelector('[data-wishlist-login-dialog]');
  const removeDialog = page.querySelector('[data-wishlist-remove-dialog]');
  const removeConfirm = removeDialog?.querySelector('[data-wishlist-remove-confirm]');
  const removeCancel = removeDialog?.querySelector('[data-wishlist-remove-cancel]');
  const toast = page.querySelector('[data-wishlist-toast]');
  let toastTimeout;
  let pendingRemove;

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(toastTimeout);
    toastTimeout = setTimeout(() => {
      toast.hidden = true;
    }, 3000);
  };

  loadProducts(page, sharedIds.length ? sharedIds : getStoredIds(), wishlistGrid).then(() => updateEmptyState(page));

  const showView = async (view) => {
    const showWishlist = view === 'wishlist';
    wishlistPanel.hidden = !showWishlist;
    recent.hidden = showWishlist;

    page.querySelectorAll('[data-wishlist-view]').forEach((tab) => {
      tab.setAttribute('aria-selected', String(tab.dataset.wishlistView === view));
    });

    if (showWishlist || recent.dataset.loaded === 'true') return;
    recent.dataset.loaded = 'true';
    const ids = RecentlyViewed.getProducts().filter((id) => !sharedIds.includes(String(id)));
    await loadProducts(page, ids, recentGrid);
    if (recentEmpty) recentEmpty.hidden = recentGrid?.querySelector('[data-wishlist-item]') !== null;
  };

  page.querySelectorAll('[data-wishlist-view]').forEach((tab) => {
    tab.addEventListener('click', () => showView(tab.dataset.wishlistView));
  });

  page.querySelector('[data-wishlist-login]')?.addEventListener('click', () => loginDialog?.showModal());
  loginDialog?.addEventListener('click', (event) => {
    if (event.target === loginDialog) loginDialog.close();
  });

  page.querySelector('[data-wishlist-share]')?.addEventListener('click', async () => {
    const ids = getStoredIds();
    if (!ids.length) {
      setStatus(page, 'Add a piece before sharing your wishlist.');
      return;
    }

    const shareUrl = new URL(window.location.href);
    shareUrl.searchParams.set('share', ids.join(','));
    const shareData = { title: 'My Tvara wishlist', url: shareUrl.toString() };

    if (navigator.share) {
      await navigator.share(shareData).catch(() => {});
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(shareUrl.toString())
        .then(() => setStatus(page, 'Wishlist link copied.'))
        .catch(() => setStatus(page, 'Could not copy wishlist link.'));
    }
  });

  page.addEventListener('click', (event) => {
    const addButton = event.target.closest('.tvara-wishlist-card__add .quick-add__button');
    if (addButton && wishlistGrid?.contains(addButton) && !addButton.classList.contains('quick-add__button--choose')) {
      addButton.dataset.wishlistPending = 'true';
    }

    const remove = event.target.closest('[data-wishlist-remove]');
    if (!remove) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    pendingRemove = remove;
    removeDialog?.showModal();
  }, true);

  removeCancel?.addEventListener('click', () => {
    pendingRemove = undefined;
    removeDialog.close();
  });

  removeConfirm?.addEventListener('click', () => {
    if (!pendingRemove) return;
    const productId = pendingRemove.dataset.wishlistRemove;
    const card = pendingRemove.closest('[data-wishlist-item]');
    removeStoredWishlistItem(productId);
    card?.remove();
    pendingRemove = undefined;
    removeDialog.close();
    requestAnimationFrame(() => updateEmptyState(page));
  });

  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise?.then(({ detail }) => {
      if (!detail?.productId) return;

      const productId = String(detail.productId);
      const card = [...(wishlistGrid?.querySelectorAll('[data-wishlist-item]') || [])].find(
        (item) => item.dataset.wishlistProductId === productId
      );
      if (!card) return;

      const addButton = card.querySelector('.tvara-wishlist-card__add .quick-add__button');
      if (detail.didError) {
        addButton?.removeAttribute('data-wishlist-pending');
        return;
      }

      removeStoredWishlistItem(productId);
      card.remove();
      updateEmptyState(page);
      showToast('Your item has been added to Cart successfully.');
    }).catch(() => {});
  });

  window.addEventListener('storage', (event) => {
    if (event.key !== 'tvara-wishlist') return;
    loadProducts(page, getStoredIds(), wishlistGrid).then(() => updateEmptyState(page));
  });

  window.addEventListener('tvara:wishlist-change', () => {
    loadProducts(page, getStoredIds(), wishlistGrid).then(() => updateEmptyState(page));
  });

  updateEmptyState(page);
};

document.querySelectorAll('[data-wishlist-page]').forEach(initWishlistPage);
